import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { formatCurrency, formatDate, generateWhatsAppUrl } from '../lib/utils'
import LoadingSpinner from '../components/LoadingSpinner'
import Footer from '../components/Footer'

function OrderSuccessPage() {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api.getOrder(orderId!),
    enabled: !!orderId,
  })

  // Redirect to payment gateway if payment URL is provided
  useEffect(() => {
    if (order?.paymentUrl && order.paymentStatus === 'unpaid') {
      // Redirect after a short delay to show the success message
      const timer = setTimeout(() => {
        window.location.href = order.paymentUrl!
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [order])

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Pesanan Tidak Ditemukan</h1>
            <p className="text-gray-600 mb-6">Maaf, pesanan tidak dapat ditemukan</p>
            <button onClick={() => navigate('/')} className="btn-primary">
              Kembali ke Toko
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const hasPaymentGateway = order.paymentGatewayUsed !== 'whatsapp'
  const isPaid = order.paymentStatus === 'paid'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-success bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold mb-2">Pesanan Berhasil Dibuat!</h1>
            <p className="text-gray-600 mb-6">
              Terima kasih atas pesanan Anda
            </p>

            <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">Nomor Pesanan</p>
                  <p className="font-mono font-semibold">#{String(order.id).slice(0, 8)}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Tanggal</p>
                  <p className="font-semibold">{formatDate(order.createdAt)}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Total</p>
                  <p className="font-mono font-bold text-primary text-lg">{formatCurrency(order.total)}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Status Pembayaran</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${isPaid ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'
                    }`}>
                    {isPaid ? 'Lunas' : 'Menunggu Pembayaran'}
                  </span>
                </div>
              </div>
            </div>


            {/* Payment Gateway Redirect */}
            {hasPaymentGateway && !isPaid && order.paymentUrl && (
              <div className="mb-6">
                <div className="bg-primary bg-opacity-10 border border-primary rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Anda akan diarahkan ke halaman pembayaran dalam beberapa detik...
                  </p>
                  <a
                    href={order.paymentUrl}
                    className="btn-primary inline-block"
                  >
                    Bayar Sekarang
                  </a>
                </div>
              </div>
            )}

            {/* WhatsApp Fallback */}
            {!hasPaymentGateway && (
              <div className="mb-6">
                <div className="bg-success bg-opacity-10 border border-success rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-700 mb-3">
                    Silakan hubungi kami melalui WhatsApp untuk menyelesaikan pembayaran
                  </p>
                  <a
                    href={generateWhatsAppUrl(
                      order.customerPhone,
                      `Halo, saya ingin melakukan pembayaran untuk pesanan #${String(order.id).slice(0, 8)} dengan total ${formatCurrency(order.total)}`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    Hubungi via WhatsApp
                  </a>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button onClick={() => navigate('/')} className="btn-secondary w-full">
                Kembali ke Toko
              </button>
            </div>
          </div>

          {/* Order Details */}
          <div className="card p-6 mt-6">
            <h2 className="text-xl font-bold mb-4">Detail Pengiriman</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-600">Nama</p>
                <p className="font-semibold">{order.customerName}</p>
              </div>
              <div>
                <p className="text-gray-600">Telepon</p>
                <p className="font-semibold">{order.customerPhone}</p>
              </div>
              <div>
                <p className="text-gray-600">Email</p>
                <p className="font-semibold">{order.customerEmail}</p>
              </div>
              <div>
                <p className="text-gray-600">Alamat</p>
                <p className="font-semibold whitespace-pre-wrap">{order.customerAddress}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default OrderSuccessPage
